import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import CustomerProfile from "@/pages/CustomerProfile";
import Products from "@/pages/Products";
import Sales from "@/pages/Sales";
import Services from "@/pages/Services";
import FollowUps from "@/pages/FollowUps";
import Enquiries from "@/pages/Enquiries";
import AccessControl from "@/pages/AccessControl";
import Invoice from "@/pages/Invoice";
import Users from "@/pages/Users";
import CustomerFields from "@/pages/CustomerFields";
import NotificationTemplates from "@/pages/NotificationTemplates";
import BusinessProfile from "@/pages/BusinessProfile";
import SecurityLog from "@/pages/SecurityLog";
import Subsidies from "@/pages/Subsidies";
import SubsidyDetail from "@/pages/SubsidyDetail";
import Trash from "@/pages/Trash";
import Engagement from "@/pages/Engagement";
import CallingDesk from "@/pages/CallingDesk";
import CallOutcomes from "@/pages/CallOutcomes";
import DataMigration, { DataMigrationBatch } from "@/pages/DataMigration";
import HistoricalProcessing, { HistoricalProcessingRecord } from "@/pages/HistoricalProcessing";
import TeamAccess from "@/pages/TeamAccess";
import Backups from "@/pages/Backups";
import NotFound from "@/pages/NotFound";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import {
  AccountsReminders, AccountsPayments, AccountsPayees,
  AccountsBanks, AccountsActivity, AccountsRecurring, AccountsGlobalChat,
} from "@/pages/AccountsPaymentDesk";
import SkeletonPage from "@/components/KovaSkeleton";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <SkeletonPage title kpi table />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

/** Accountants should never land on the general ERP dashboard. */
function HomeRedirect() {
  const { user } = useAuth();
  if (user?.role === "accountant") {
    return <Navigate to="/accounts/reminders" replace />;
  }
  return <Dashboard />;
}

function GuestOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <SkeletonPage title kpi table />;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App dark min-h-screen bg-[#0B0F0C] text-[#F8F9FA]">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public — no auth required (needed for Google OAuth consent-screen verification). */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
            <Route element={<Protected><Layout /></Protected>}>
              <Route path="/" element={<HomeRedirect />} />
              <Route path="/customers" element={<Customers />} />
              <Route path="/customers/:id" element={<CustomerProfile />} />
              <Route path="/products" element={<Products />} />
              <Route path="/sales" element={<Sales />} />
              <Route path="/sales/:id/invoice" element={<Invoice />} />
              <Route path="/services" element={<Services />} />
              <Route path="/followups" element={<FollowUps />} />
              <Route path="/crm/enquiries" element={<Enquiries />} />
              <Route path="/admin/access-control" element={<AccessControl />} />
              <Route path="/users" element={<Users />} />
              <Route path="/settings/customer-fields" element={<CustomerFields />} />
              <Route path="/settings/notifications" element={<NotificationTemplates />} />
              <Route path="/settings/business" element={<BusinessProfile />} />
              <Route path="/settings/security-log" element={<SecurityLog />} />
              <Route path="/subsidies" element={<Subsidies />} />
              <Route path="/subsidies/:id" element={<SubsidyDetail />} />
              <Route path="/trash" element={<Trash />} />
              <Route path="/engagement" element={<Engagement />} />
              <Route path="/engagement/campaigns/:id" element={<CallingDesk />} />
              <Route path="/settings/call-outcomes" element={<CallOutcomes />} />
              <Route path="/admin/data-migration" element={<DataMigration />} />
              <Route path="/admin/data-migration/:id" element={<DataMigrationBatch />} />
              <Route path="/admin/historical-processing" element={<HistoricalProcessing />} />
              <Route path="/admin/historical-processing/:id" element={<HistoricalProcessingRecord />} />
              <Route path="/admin/team-access" element={<TeamAccess />} />
              <Route path="/admin/backups" element={<Backups />} />
              <Route path="/accounts/reminders" element={<AccountsReminders />} />
              <Route path="/accounts/payments"  element={<AccountsPayments />} />
              <Route path="/accounts/payees"    element={<AccountsPayees />} />
              <Route path="/accounts/banks"     element={<AccountsBanks />} />
              <Route path="/accounts/activity"  element={<AccountsActivity />} />
              <Route path="/accounts/recurring" element={<AccountsRecurring />} />
              <Route path="/accounts/chat"      element={<AccountsGlobalChat />} />
              {/* Catch-all inside the app shell — shows the KOVA 404 with sidebar + topbar intact. */}
              <Route path="*" element={<NotFound />} />
            </Route>
            {/* Catch-all outside the app shell (unauthenticated) — routes to login. */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
